import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { database } from "../../../firebaseConfig";
import { ref, onValue, get } from "firebase/database";
import OfferPreviewPage from "../OfferPreviewPage";
import "../OfferPreviewPage/OfferPreviewPage.css";
import "./CustomOfferView.css";
import { Helmet } from "react-helmet-async";

/**
 * CustomOfferView loads publicly shared offers via a customId
 * and uses OfferPreviewPage for display
 */
const CustomOfferView = () => {
  const { customId } = useParams();
  const [offerData, setOfferData] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState({
    title: "Custom Document Details",
    description: "View the details of your tailored document, including features, scope, and pricing — created to match your specific business requirements.",
    imageUrl: null
  });

  useEffect(() => {
    // Validate and extract userId and offerId from the customId
    if (!customId || !customId.includes("+")) {
      setError("Invalid offer link");
      setLoading(false);
      return;
    }

    const [userId, offerId] = customId.split("+");

    if (!userId || !offerId) {
      setError("Invalid offer link");
      setLoading(false);
      return;
    }

    const loadTeamMemberData = async (teamMemberId) => {
      try {
        const teamMemberRef = ref(database, `/World/${userId}/team/members/${teamMemberId}`);
        const snapshot = await get(teamMemberRef);
        if (snapshot.exists()) {
          return snapshot.val();
        }
        return null;
      } catch (error) {
        console.error("Error loading team member data:", error);
        return null;
      }
    };

    // Try to load from public offers first
    const publicOfferRef = ref(database, `/publicOffers/${customId}`);
    
    const unsubscribe = onValue(publicOfferRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        
        setOfferData(data);
        
        // Set meta data for social media preview
        const documentTypeName = data.documentType === 'invoice' ? 'Invoice' : 'Offer';
        setMetaData({
          title: data.title || `${documentTypeName}: ${data.name || (data.documentType === 'invoice' ? 'Custom Invoice' : 'Custom Offer')}`,
          description: data.description || `A tailored ${documentTypeName.toLowerCase()}${data.teamMember ? ` from ${data.teamMember.name}` : ''}`,
          imageUrl: data.imageUrl || (data.teamMember && data.teamMember.profileImageUrl) || null
        });
        
        // Services are already included in the public offer data
        // Apply serviceOverrides, microserviceOverrides, removedMicroservices, and additionalMicroservices
        if (data.services) {
          const processedServices = data.services.map(service => {
            // Apply service-level overrides (price, title, description, isRecurring)
            const serviceOverride = data.serviceOverrides?.[service.id] || {};
            
            // Get removed microservices for this service
            const removedMs = data.removedMicroservices?.[service.id] || [];
            
            // Get additional microservices for this service
            const additionalMsIds = data.additionalMicroservices?.[service.id] || [];
            
            // Get microservice overrides for this service
            const msOverrides = data.microserviceOverrides?.[service.id] || {};
            
            // Process microservices
            let processedMicroservices = [];
            if (service.microservices) {
              // Filter out removed microservices
              const filteredMicroservices = service.microservices.filter(ms => {
                const msId = typeof ms === 'object' ? ms.id : ms;
                return !removedMs.includes(msId);
              });
              
              // Apply microservice overrides
              processedMicroservices = filteredMicroservices.map(ms => {
                const msData = typeof ms === 'object' ? ms : { id: ms, title: ms };
                const override = msOverrides[msData.id] || {};
                return {
                  ...msData,
                  title: override.title || msData.title,
                  description: override.description || msData.description
                };
              });
            }
            
            // Note: Additional microservices would need to be loaded separately if they're just IDs
            // For now, we skip them as the public offer should have the full data
            
            // Resolve effective payment type:
            //   override.paymentType > legacy override.isRecurring > service.paymentType > service.isRecurring
            let effectivePaymentType;
            if (serviceOverride.paymentType !== undefined) {
              effectivePaymentType = serviceOverride.paymentType;
            } else if (serviceOverride.isRecurring !== undefined) {
              effectivePaymentType = serviceOverride.isRecurring ? 'recurring' : 'oneTime';
            } else if (service.paymentType) {
              effectivePaymentType = service.paymentType;
            } else {
              effectivePaymentType = service.isRecurring ? 'recurring' : 'oneTime';
            }

            return {
              ...service,
              title: serviceOverride.title || service.title,
              description: serviceOverride.description || service.description,
              price: serviceOverride.price !== undefined ? serviceOverride.price : service.price,
              paymentType: effectivePaymentType,
              isRecurring: effectivePaymentType === 'recurring',
              microservices: processedMicroservices
            };
          });
          
          setServices(processedServices);
        }
        
        setLoading(false);
      } else {
        setError("Document not found or not public");
        setLoading(false);
      }
    }, (error) => {
      console.error("Error loading offer:", error);
      setError("Offer could not be loaded");
      setLoading(false);
    });

    return () => unsubscribe();
  }, [customId]);

  // Show loader, error, or empty state
  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading offer...</title>
          <meta name="description" content="Please wait, the offer is loading." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__loading">
            <div className="offer-preview-page__spinner"></div>
            <p>Loading offer...</p>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Error loading offer</title>
          <meta name="description" content="The offer could not be loaded." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__error">
            <span className="offer-preview-page__error-icon">⚠️</span>
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        </div>
      </>
    );
  }

  if (!offerData) {
    return (
      <>
        <Helmet>
          <title>Offer not found</title>
          <meta name="description" content="The requested offer is not available." />
        </Helmet>
        <div className="offer-preview-page offer-preview-page--only-preview">
          <div className="offer-preview-page__empty">
            <span className="offer-preview-page__empty-icon">📄</span>
            <h3>Offer not found</h3>
            <p>The requested offer is not available.</p>
          </div>
        </div>
      </>
    );
  }

  // Use the OfferPreviewPage component for display
  return (
    <>
      <Helmet>
        <title>{metaData.title}</title>
        <meta name="description" content={metaData.description} />
        
        {/* Open Graph Meta Tags for Facebook, WhatsApp etc. */}
        <meta property="og:title" content={metaData.title} />
        <meta property="og:description" content={metaData.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Bonus Club" />
        {metaData.imageUrl ? (
          <meta property="og:image" content={metaData.imageUrl} />
        ) : (
          <meta property="og:image" content={`${window.location.origin}/og-image.jpg`} />
        )}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaData.title} />
        <meta name="twitter:description" content={metaData.description} />
        <meta name="twitter:site" content="@bonusclub" />
        {metaData.imageUrl ? (
          <meta name="twitter:image" content={metaData.imageUrl} />
        ) : (
          <meta name="twitter:image" content={`${window.location.origin}/og-image.jpg`} />
        )}
        
        {/* Additional meta for better crawling */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      
      <div className="custom-offer-view">
        <OfferPreviewPage 
          offerData={{
            ...offerData,
            teamMember: offerData.teamMember ? {
              ...offerData.teamMember,
              signature: offerData.teamMember.signature,
              signatureMaxWidth: offerData.teamMember.signatureMaxWidth || 150,
              name: offerData.teamMember.name,
              companyName: offerData.teamMember.companyName,
              companySlogan: offerData.teamMember.companySlogan,
              personalEmail: offerData.teamMember.personalEmail,
              generalEmail: offerData.teamMember.generalEmail,
              phone: offerData.teamMember.phone,
              website: offerData.teamMember.website,
              bankName: offerData.teamMember.bankName,
              iban: offerData.teamMember.iban,
              bic: offerData.teamMember.bic
            } : null
          }}
          services={services} 
          onlyPreview={true} 
        />
      </div>
    </>
  );
};

export default CustomOfferView; 